import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertTriangle, Trash2, Search, Bell, RefreshCw, Mail, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import WeeklyDigestSubTab from '@/components/admin/WeeklyDigestSubTab';

/**
 * Admin > Notifications
 * Top-level tabs:
 *   - Cleanup: prune existing in-app notifications
 *   - Weekly Email: configure / manage the weekly digest blast
 *   - Reminders: choose session-reminder channel (email / sms / both)
 */
export function AdminNotificationsTab() {
  return (
    <Tabs defaultValue="cleanup" className="w-full" data-testid="admin-notifications-tabs">
      <TabsList className="mb-4">
        <TabsTrigger value="cleanup" data-testid="admin-notif-tab-cleanup">
          <Bell className="h-4 w-4 mr-1.5" /> Cleanup
        </TabsTrigger>
        <TabsTrigger value="weekly" data-testid="admin-notif-tab-weekly">
          <Mail className="h-4 w-4 mr-1.5" /> Weekly Email
        </TabsTrigger>
        <TabsTrigger value="reminders" data-testid="admin-notif-tab-reminders">
          <MessageSquare className="h-4 w-4 mr-1.5" /> Reminders
        </TabsTrigger>
      </TabsList>
      <TabsContent value="cleanup">
        <CleanupSubTab />
      </TabsContent>
      <TabsContent value="weekly">
        <WeeklyDigestSubTab />
      </TabsContent>
      <TabsContent value="reminders">
        <RemindersSubTab />
      </TabsContent>
    </Tabs>
  );
}


function RemindersSubTab() {
  const [channel, setChannel] = useState('email');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/admin/settings/reminders', { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setChannel(d.channel || 'email');
        }
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await fetch('/api/admin/settings/reminders', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel: next }),
      });
      if (!r.ok) throw new Error('failed');
      const d = await r.json();
      setChannel(d.channel);
      toast.success('Reminder channel saved');
    } catch { toast.error('Could not save'); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;

  const options = [
    { value: 'email', label: 'Email only', icon: Mail, hint: 'Attendees get an email 10 minutes before each starred session (via SendGrid). Default.' },
    { value: 'sms',   label: 'SMS only',   icon: MessageSquare, hint: 'Opted-in attendees get a text message 10 minutes before each starred session. Requires Bird.com credit + alpha-sender registration.' },
    { value: 'both',  label: 'Email + SMS', icon: Bell, hint: 'Both channels fire for maximum reach.' },
  ];

  return (
    <Card className="p-5" data-testid="reminders-settings-card">
      <h3 className="text-base font-semibold text-slate-800 mb-1">Session reminder channel</h3>
      <p className="text-xs text-slate-500 mb-4">Controls how 10-minute pre-session reminders go out for sessions attendees have starred in <em>My Agenda</em>.</p>
      <div className="space-y-2">
        {options.map(o => {
          const Icon = o.icon;
          const active = channel === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => save(o.value)}
              disabled={saving}
              data-testid={`reminder-channel-${o.value}`}
              className={`w-full text-left p-3 rounded-lg border-2 transition-all flex items-start gap-3
                ${active ? 'border-brand-navy bg-brand-navy/5' : 'border-slate-200 hover:border-brand-navy/30'}`}>
              <div className={`p-2 rounded-full ${active ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-500'}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`font-semibold text-sm ${active ? 'text-brand-navy' : 'text-slate-700'}`}>{o.label}</span>
                  {active && <span className="text-[10px] font-bold px-1.5 py-0.5 bg-brand-navy text-white rounded-full">ACTIVE</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{o.hint}</p>
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-400 mt-4">
        Email reminders use the existing SendGrid integration (no extra cost). SMS reminders use Bird.com — currently inactive until credit is added and the alpha sender "BriteHub" is UK-approved.
      </p>
    </Card>
  );
}


function CleanupSubTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [pending, setPending] = useState(null); // { kind, target, count, label }
  const [working, setWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch('/api/admin/notifications/stats', { credentials: 'include' });
      if (!r.ok) throw new Error('Failed');
      setStats(await r.json());
    } catch (e) { toast.error('Failed to load notification stats'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const runDelete = async () => {
    if (!pending) return;
    setWorking(true);
    try {
      const body = { confirm: true };
      if (pending.kind === 'type') body.type = pending.target;
      else if (pending.kind === 'user') body.user_id = pending.target;
      else if (pending.kind === 'all') body.delete_all = true;
      const r = await fetch('/api/admin/notifications/delete', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Deleted ${d.deleted} notification${d.deleted === 1 ? '' : 's'}`);
      setPending(null);
      load();
    } catch (e) { toast.error(e.message || 'Delete failed'); }
    setWorking(false);
  };

  if (loading) return <div className="text-slate-400 text-center py-12">Loading…</div>;
  if (!stats) return <div className="text-slate-400 text-center py-12">No data</div>;

  const filteredUsers = (stats.by_user || []).filter(u => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return (u.name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.user_id || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="admin-notifications-tab">
      {/* Header + totals */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold font-heading flex items-center gap-2">
            <Bell className="h-6 w-6 text-brand-navy" /> Notifications Management
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Prune in-app notifications by type or recipient. Deletions are permanent — be sure
            before confirming.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button size="sm" variant="ghost" onClick={load} data-testid="notifs-refresh">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-rose-200 text-rose-600 hover:bg-rose-50"
            onClick={() => setPending({ kind: 'all', target: null, count: stats.total, label: 'EVERY notification in the system' })}
            data-testid="notifs-delete-all"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete all
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Total notifications</p>
          <p className="text-2xl font-bold tabular-nums" data-testid="notifs-total">{stats.total.toLocaleString()}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Unread</p>
          <p className="text-2xl font-bold tabular-nums text-amber-600">{stats.unread.toLocaleString()}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Types</p>
          <p className="text-2xl font-bold tabular-nums">{stats.by_type.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Recipients (top 50)</p>
          <p className="text-2xl font-bold tabular-nums">{stats.by_user.length}</p>
        </Card>
      </div>

      {/* By type */}
      <Card className="p-5">
        <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-3">By Type</h3>
        {stats.by_type.length === 0 ? (
          <p className="text-slate-400 text-sm">No notifications.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {stats.by_type.map((t) => (
              <div key={t.type} className="flex items-center justify-between py-2.5" data-testid={`notifs-type-row-${t.type}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">{t.type}</p>
                  <p className="text-xs text-slate-500">{t.count.toLocaleString()} total · {t.unread} unread</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  onClick={() => setPending({ kind: 'type', target: t.type, count: t.count, label: `all notifications of type "${t.type}"` })}
                  data-testid={`notifs-delete-type-${t.type}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* By user */}
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">By Recipient (top 50)</h3>
        </div>
        <div className="relative mb-3">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by name, email or user id…"
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            className="pl-9"
            data-testid="notifs-user-search"
          />
        </div>
        {filteredUsers.length === 0 ? (
          <p className="text-slate-400 text-sm py-3">No matches.</p>
        ) : (
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {filteredUsers.map(u => (
              <div key={u.user_id} className="flex items-center justify-between py-2.5" data-testid={`notifs-user-row-${u.user_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                  <p className="text-xs text-slate-400">{u.count} notifications · {u.unread} unread</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-rose-200 text-rose-600 hover:bg-rose-50"
                  onClick={() => setPending({ kind: 'user', target: u.user_id, count: u.count, label: `all ${u.count} notifications for ${u.name}` })}
                  data-testid={`notifs-delete-user-${u.user_id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Confirmation warning */}
      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent className="max-w-md" data-testid="notifs-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-heading flex items-center gap-2 text-rose-700">
              <AlertTriangle className="h-5 w-5" /> Confirm permanent deletion
            </DialogTitle>
          </DialogHeader>
          {pending && (
            <div className="space-y-4 mt-2">
              <div className="bg-rose-50 border-l-4 border-rose-500 rounded-r-lg p-4">
                <p className="text-sm text-rose-900 font-medium">
                  You're about to delete <span className="font-bold">{(pending.count || 0).toLocaleString()}</span> notification{pending.count === 1 ? '' : 's'}.
                </p>
                <p className="text-sm text-rose-800 mt-1">
                  Target: <strong>{pending.label}</strong>
                </p>
              </div>
              <p className="text-xs text-slate-500">
                This action cannot be undone. Affected users will see these notifications
                disappear from their bell on next refresh.
              </p>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-slate-100">
            <Button variant="ghost" onClick={() => setPending(null)} disabled={working} data-testid="notifs-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={runDelete}
              disabled={working}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="notifs-confirm-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {working ? 'Deleting…' : 'Yes, delete permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
